import { useEffect, useMemo } from "react";
import { useImageFilesQueries } from "shared/queries/useImageFileQueries";
import { ModuleContentBlocksContent, CardBlockImage } from "../types";

type UploadImagesProps = {
  key: string;
  moduleId: string | null;
  setCardBlockImages: (images: CardBlockImage[]) => void;
  contentBlocks?: ModuleContentBlocksContent[];
  enabled: boolean;
};

type UploadImagesOutput = { loadingQueries: Record<number, boolean> };

export const useUploadContentBlocksImages = ({
  key,
  moduleId,
  setCardBlockImages,
  contentBlocks = [],
  enabled,
}: UploadImagesProps): UploadImagesOutput => {
  const queries = useMemo(() => {
    return contentBlocks
      ?.map((block, index) => ({
        queryKey: [`${key}-${index}-image`, moduleId ?? ""],
        path: block.backgroundImage,
      }))
      .filter((query) => query.path);
  }, [contentBlocks, key, moduleId]);

  const queriesResults = useImageFilesQueries({
    queries,
    enabled,
  });

  const images = queriesResults?.map(({ data = null }) => data).filter(Boolean);
  const loadingStates = queriesResults?.map((query) => query.isFetching);

  useEffect(() => {
    if (enabled && images.length && !loadingStates.every(Boolean)) {
      setCardBlockImages(images);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, images.length, loadingStates]);

  const loadingQueries: Record<number, boolean> = loadingStates.reduce(
    (acc, state, index) => ({ ...acc, [index]: state }),
    {},
  );

  return { loadingQueries };
};
